<template>
    <div
        ref="tableContainer"
        v-bind:class="{ 'sticky-cols': useStickyCols, 'hide-scroll': useGlobalScroll }"
        class="table-responsive overflow-auto flex-grow-1 position-relative">
        <table class="table table-hover table-sm">
            <thead>
                <tr>
                    <th
                        v-for="(column, colIndex) in visibleColumns"
                        ref="headers"
                        v-bind:key="'column-header' + colIndex"
                        v-bind:class="getHeaderCellClassObject(column, colIndex)"
                        v-bind:style="getCellStyleObject(column, colIndex)"
                        v-on:click="sortClick(column.originalIndex, $event)">
                        <pc-label
                            v-bind:label="column.header"
                            v-bind:show-required="column.showRequired"
                            v-bind:show-help="column.showHelp"
                            v-on:help-clicked="helpClick(column.originalIndex, $event)" />
                    </th>
                </tr>
            </thead>
            <tbody v-if="items.length > 0">
                <template
                    v-for="(item, rowIndex) in items"
                    v-bind:key="'row' + rowIndex">
                    <tr
                        v-bind:id="item.id"
                        v-bind:class="{ 'expanded-parent': showActions(item.item) && item.expanded }"
                        v-bind:data-test-state="showActions(item.item) ? 'expandable' : ''"
                        data-test-role="table-row"
                        v-on:click="rowClick(rowIndex)">
                        <td
                            v-for="(column, colIndex) in visibleColumns"
                            v-bind:key="getCellKey(rowIndex, colIndex, item)"
                            v-bind:class="getCellClassObject(column, colIndex, showActions(item.item))"
                            v-bind:style="getCellStyleObject(column, colIndex)"
                            v-on:click="cellClick(colIndex, item)">
                            <div class="d-flex">
                                <div
                                    v-if="showKebab && showActions(item.item) && colIndex === 0"
                                    class="dropdown d-flex">
                                    <button
                                        type="button"
                                        class="btn border-0 py-0 shadow-none"
                                        data-bs-toggle="dropdown">
                                        <pc-bs-icon
                                            name="three-dots"
                                            tabindex="0" />
                                    </button>
                                    <slot
                                        name="contextMenu"
                                        v-bind="item.item" />
                                </div>
                                <div
                                    v-else-if="showActions(item.item) && showChevron(item.item) && colIndex === 0"
                                    class="d-inline-block pe-3 align-self-center">
                                    <pc-bs-icon
                                        name="chevron-right"
                                        v-bind:rotate-on="item.expanded"
                                        tabindex="0"
                                        v-on:keyup.enter="rowClick(rowIndex)" />
                                </div>
                                <div
                                    class="flex-grow-1"
                                    style="flex-basis: 100%"
                                    v-bind:data-test-role="`cell_${column.property}`">
                                    <slot
                                        v-bind:name="column.property"
                                        v-bind="item.item" />
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr
                        v-if="showActions(item.item)"
                        v-bind:key="'row-expanded' + rowIndex"
                        v-bind:data-test-state="item.expanded ? 'expanded' : 'collapsed'"
                        data-test-role="table-expanded-row"
                        class="expanded-child">
                        <template v-if="useStickyCols">
                            <td
                                v-for="n in numberOfStickyColumns"
                                v-bind:key="n"
                                v-bind:class="getCellClassObject(visibleColumns[n - 1], n - 1)"
                                v-bind:style="getCellStyleObject(visibleColumns[n - 1], n - 1)"
                                class="p-0" />
                        </template>
                        <td
                            v-bind:colspan="colSpanForActionRow"
                            class="p-0">
                            <pc-transition
                                transition="slide"
                                appear>
                                <div
                                    v-if="item.expanded"
                                    class="overflow-hidden">
                                    <div
                                        v-bind:style="actionRowStyleObj"
                                        class="px-3 py-2 actions-row-div">
                                        <slot
                                            v-bind="item.item"
                                            name="actionsRow" />
                                    </div>
                                </div>
                            </pc-transition>
                        </td>
                    </tr>
                </template>
            </tbody>
            <tbody v-else>
                <tr>
                    <td colspan="100%">
                        <slot name="emptyContent" />
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="showFooter()">
                <slot name="footer" />
            </tfoot>
        </table>
    </div>
</template>

<script>
    import commonOptions from "./_common.js";
    import { isIE } from "Util/util";
    import { responsiveManager } from "Responsive/manager";

    export default {
        extends: commonOptions,
        props: {
            useGlobalScroll: { type: Boolean, required: false, default: false },
            showKebab: { type: Boolean, required: false, default: false },
            showChevron: { type: Function, required: false, default: () => true },
            showFooter: { type: Function, required: false, default: () => false }
        },
        data: function () {
            return {
                tableContainerScrollLeft: 0,
                needsHorizontalScrolling: false,
                stickyColOffsets: null
            };
        },
        computed: {
            useStickyCols() {
                return this.needsHorizontalScrolling && this.useGlobalScroll && !isIE();
            },
            actionRowStyleObj() {
                return {
                    position: "relative",
                    left: `${this.tableContainerScrollLeft}px`
                };
            },
            visibleColumns() {
                const filtered = [];
                let filteredIndex = 0;
                for (let index = 0; index < this.columns.length; index++) {
                    if (this.columns[index].visible) {
                        filtered.push({ ...this.columns[index] });
                        filtered[filteredIndex].originalIndex = index;
                        filteredIndex++;
                    }
                }
                return filtered;
            },
            colSpanForActionRow() {
                return this.numberOfColumns - this.numberOfStickyColumns;
            },
            numberOfColumns() {
                return this.visibleColumns.length;
            },
            numberOfStickyColumns() {
                if (!this.useStickyCols) {
                    return 0;
                }
                return this.visibleColumns.filter((c) => c.sticky).length;
            }
        },
        async created() {
            await this.$nextTick();
            if (this.useGlobalScroll) {
                if (this.$refs.tableContainer) {
                    this.$app.hoistElementToGlobalScroll(this.$refs.tableContainer);
                    responsiveManager.addResizeListener(this.calcSize);
                    this.$refs.tableContainer.addEventListener("scroll", () => {
                        if (this.useStickyCols) {
                            window.requestAnimationFrame(() => {
                                this.tableContainerScrollLeft = this.$refs.tableContainer.scrollLeft;
                            });
                        }
                    });

                    this.calcSize();
                }
            }
        },
        beforeUnmount() {
            if (this.useGlobalScroll) {
                this.$app.hoistElementToGlobalScroll(null);
            }
        },
        methods: {
            getHeaderCellClassObject(column, colIndex) {
                return {
                    ...this.getClassObject(column),
                    [column.headerClass]: true,
                    ["sticky-col-cell"]: column.sticky,
                    ["right-box-shadow-border"]: colIndex === this.numberOfStickyColumns - 1
                };
            },
            getCellClassObject(column, colIndex, hideBottomBorder = false) {
                return {
                    [column.columnClass]: true,
                    ["sticky-col-cell"]: column.sticky,
                    ["right-box-shadow-border"]: colIndex === this.numberOfStickyColumns - 1,
                    "border-bottom-0": hideBottomBorder
                };
            },
            getCellStyleObject(column, colIndex) {
                if (this.stickyColOffsets && this.stickyColOffsets[colIndex] > 0 && column.sticky) {
                    return {
                        left: `${this.stickyColOffsets[colIndex]}px`
                    };
                }
            },
            calcSize() {
                this.stickyColOffsets = null;
                if (!this.$refs.tableContainer) {
                    this.needsHorizontalScrolling = false;
                    return;
                }
                this.needsHorizontalScrolling =
                    this.$refs.tableContainer.scrollWidth > this.$refs.tableContainer.clientWidth;

                if (this.numberOfStickyColumns > 0) {
                    this.stickyColOffsets = [0];
                    for (let i = 1; i < this.numberOfStickyColumns; i++) {
                        if (this.$refs.headers[i]) {
                            this.stickyColOffsets.push(
                                this.stickyColOffsets[i - 1] + this.$refs.headers[i - 1].clientWidth
                            );
                        } else {
                            this.stickyColOffsets.push(0);
                        }
                    }
                }
            },
            hoistToGlobalScroll() {
                this.$app.hoistElementToGlobalScroll(this.$refs.tableContainer);
            }
        }
    };
</script>

<style lang="scss" scoped>
    $pc-border-width: 3px;

    table tr {
        background-color: var(--bs-white);
    }
    table td,
    table th {
        background-color: inherit;
    }

    tr.expanded-parent,
    tr.expanded-child {
        background-color: var(--bs-primary-050);
    }

    tr.expanded-child > td,
    tr.expanded-parent > td {
        position: relative;
    }

    tr.expanded-child:hover,
    tr.expanded-parent:hover {
        background-color: var(--bs-primary-100);
    }

    tr.expanded-parent > td:after,
    tr.expanded-child > td:after {
        position: absolute;
        top: 0;
        left: 0;
        display: block;
        content: " ";
        width: 100%;
        height: 100%;
        pointer-events: none;
    }

    thead > tr > th.sortable-header {
        vertical-align: baseline;
        position: relative;
        padding-right: 25px;
        cursor: pointer;
    }
    thead > tr > th.sortable-header:hover {
        background-color: var(--bs-gray-100);
    }
    thead > tr > th.sortable-header:before,
    thead > tr > th.sortable-header:after {
        position: absolute;
        top: 0;
        display: block;
        opacity: 0.4;
        padding-top: inherit;
        padding-bottom: inherit;
        font-size: inherit;
    }
    thead > tr > th.sortable-header:before {
        right: 0.75em;
        content: "\25B2"; /*unicode for triangle up*/
    }
    thead > tr > th.sortable-header.sorting-asc:before {
        opacity: 1;
    }
    thead > tr > th.sortable-header.sorting-desc:after {
        opacity: 1;
    }
    thead > tr > th.sortable-header:after {
        right: 0.25em;
        content: "\25BC"; /*unicode for triangle down*/
    }

    .hide-scroll {
        &::-webkit-scrollbar {
            display: none;
        }
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    $_table-right-shadow-border: inset -1px 0 0 var(--bs-gray-600);

    .right-box-shadow-border {
        box-shadow: $_table-right-shadow-border;
    }
    .sticky-col-cell {
        position: sticky !important;
        left: 0;
        z-index: 1;
    }
</style>
